/**
 * The tones the notification bell can play, synthesized with the Web Audio API:
 * no audio files to ship, nothing to download before the first sound.
 *
 * The seven tones are the ones Daniel shortlisted on 2026-11-03 out of 24 options;
 * "Doble suave" is the default.
 */

/** Ids are persisted in the browser: rename one only with a fallback for the old value. */
export type NotificationToneId =
  | 'soft-double'
  | 'marimba-double'
  | 'crystal-double'
  | 'marimba-triple'
  | 'glass'
  | 'double-pop'
  | 'digital';

export interface NotificationTone {
  id: NotificationToneId;
  /** i18n key of the name shown in Preferences. */
  label: string;
  /** Schedules the tone on `out`, starting at `start` (seconds, the context clock). */
  play: (context: BaseAudioContext, out: AudioNode, start: number) => void;
}

export const DEFAULT_NOTIFICATION_TONE: NotificationToneId = 'soft-double';

interface VoiceOptions {
  type?: OscillatorType;
  peak?: number;
  attack?: number;
  /** Glide to this frequency over `bendTime` seconds. */
  bendTo?: number;
  bendTime?: number;
  /** Low-pass cutoff in Hz, to soften square waves. */
  lowpass?: number;
}

/** One oscillator with a quick attack and an exponential fade. */
function voice(
  context: BaseAudioContext,
  out: AudioNode,
  frequency: number,
  start: number,
  duration: number,
  options: VoiceOptions = {}
): void {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = options.type ?? 'sine';
  oscillator.frequency.setValueAtTime(frequency, start);
  if (options.bendTo) {
    oscillator.frequency.exponentialRampToValueAtTime(
      options.bendTo,
      start + (options.bendTime ?? duration * 0.6)
    );
  }
  const peak = options.peak ?? 0.15;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + (options.attack ?? 0.008));
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  if (options.lowpass) {
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = options.lowpass;
    oscillator.connect(filter);
    filter.connect(gain);
  } else {
    oscillator.connect(gain);
  }
  gain.connect(out);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.05);
}

/** A struck wooden bar: the fundamental plus two short partials. */
function marimba(
  context: BaseAudioContext,
  out: AudioNode,
  frequency: number,
  start: number,
  peak = 0.16
): void {
  voice(context, out, frequency, start, 0.45, { peak });
  voice(context, out, frequency * 4, start, 0.08, { peak: peak * 0.35 });
  voice(context, out, frequency * 10, start, 0.03, { peak: peak * 0.12 });
}

/** A rubbed glass: long fundamental, inharmonic partials that die fast. */
function glass(
  context: BaseAudioContext,
  out: AudioNode,
  frequency: number,
  start: number,
  peak = 0.11
): void {
  voice(context, out, frequency, start, 1.4, { peak, attack: 0.004 });
  voice(context, out, frequency * 2.76, start, 0.5, { peak: peak * 0.35 });
  voice(context, out, frequency * 5.4, start, 0.2, { peak: peak * 0.15 });
}

const NOTE = {
  E5: 659.25,
  G5: 783.99,
  A5: 880,
  B5: 987.77,
  C6: 1046.5,
  D6: 1174.66,
  E6: 1318.51,
  G6: 1567.98,
  C7: 2093,
};

export const NOTIFICATION_TONES: readonly NotificationTone[] = [
  {
    id: 'soft-double',
    label: 'NOTIFICATION_TONE_SOFT_DOUBLE',
    play: (context, out, start) => {
      voice(context, out, NOTE.E5, start, 0.35, { peak: 0.17 });
      voice(context, out, NOTE.B5, start + 0.16, 0.5, { peak: 0.15 });
    },
  },
  {
    id: 'marimba-double',
    label: 'NOTIFICATION_TONE_MARIMBA_DOUBLE',
    play: (context, out, start) => {
      marimba(context, out, NOTE.E5, start);
      marimba(context, out, NOTE.B5, start + 0.12);
    },
  },
  {
    id: 'crystal-double',
    label: 'NOTIFICATION_TONE_CRYSTAL_DOUBLE',
    play: (context, out, start) => {
      glass(context, out, NOTE.C6, start, 0.09);
      glass(context, out, NOTE.G6, start + 0.12, 0.09);
    },
  },
  {
    id: 'marimba-triple',
    label: 'NOTIFICATION_TONE_MARIMBA_TRIPLE',
    play: (context, out, start) => {
      [NOTE.G5, NOTE.B5, NOTE.D6].forEach((frequency, index) =>
        marimba(context, out, frequency, start + index * 0.09, 0.14)
      );
    },
  },
  {
    id: 'glass',
    label: 'NOTIFICATION_TONE_GLASS',
    play: (context, out, start) => glass(context, out, NOTE.C7 / 1.5, start, 0.12),
  },
  {
    id: 'double-pop',
    label: 'NOTIFICATION_TONE_DOUBLE_POP',
    play: (context, out, start) => {
      voice(context, out, 380, start, 0.1, { peak: 0.2, bendTo: 760, bendTime: 0.04 });
      voice(context, out, 520, start + 0.1, 0.12, { peak: 0.2, bendTo: 1100, bendTime: 0.05 });
    },
  },
  {
    id: 'digital',
    label: 'NOTIFICATION_TONE_DIGITAL',
    play: (context, out, start) => {
      voice(context, out, NOTE.A5, start, 0.1, { type: 'square', peak: 0.06, lowpass: 2200 });
      voice(context, out, NOTE.E6, start + 0.11, 0.14, {
        type: 'square',
        peak: 0.06,
        lowpass: 2600,
      });
    },
  },
];

/** True for an id this build knows how to play. */
export function isNotificationToneId(value: unknown): value is NotificationToneId {
  return NOTIFICATION_TONES.some((tone) => tone.id === value);
}

/** The tone with that id, or the default for an unknown one. */
export function notificationTone(id: NotificationToneId): NotificationTone {
  return (
    NOTIFICATION_TONES.find((tone) => tone.id === id) ??
    NOTIFICATION_TONES.find((tone) => tone.id === DEFAULT_NOTIFICATION_TONE)!
  );
}
